import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { X, Send, Trash2, Heart, ChevronDown, ChevronUp, Reply, Pin, ArrowUpDown, Flag, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import UserAvatar from '@/components/UserAvatar';
import { getUserById, getAllUsers } from '@/store/users';
import { useSession } from '@/store/session';
import type { FeedComment, CommentMention, CommentReportReason } from '@/store/feed';
import { cn } from '@/lib/utils';
import { formatDistanceToNowStrict } from 'date-fns';
import { toast } from 'sonner';
import { usePreferences } from '@/store/preferences';
import { moderateText, moderationMessage } from '@/utils/contentModeration';

type SortMode = 'top' | 'newest';

const REPORT_REASONS: { value: CommentReportReason; label: string }[] = [
  { value: 'spam', label: 'Spam' },
  { value: 'harassment', label: 'Harassment' },
  { value: 'insult', label: 'Insult / Abuse' },
  { value: 'inappropriate', label: 'Inappropriate content' },
  { value: 'off-topic', label: 'Off-topic' },
  { value: 'other', label: 'Other' },
];

interface CommentModalProps {
  open: boolean;
  onClose: () => void;
  comments: FeedComment[];
  onSubmit: (text: string, parentCommentId?: string, mentions?: CommentMention[]) => void;
  onDeleteComment?: (commentId: string) => void;
  onToggleCommentLike?: (commentId: string) => void;
  onPinComment?: (commentId: string | undefined) => void;
  onReportComment?: (commentId: string, reason: CommentReportReason) => void;
  onHideComment?: (commentId: string) => void;
  pinnedCommentId?: string;
  postAuthorName: string;
  postAuthorId?: string;
  currentUserId?: string;
  canPostComment?: (text: string) => { allowed: boolean; message?: string };
  onRecordCommentTime?: (text: string) => void;
}

const formatTime = (iso: string) => {
  try {
    return formatDistanceToNowStrict(new Date(iso), { addSuffix: false })
      .replace(' seconds', 's').replace(' second', 's')
      .replace(' minutes', 'm').replace(' minute', 'm')
      .replace(' hours', 'h').replace(' hour', 'h')
      .replace(' days', 'd').replace(' day', 'd')
      .replace(' weeks', 'w').replace(' week', 'w')
      .replace(' months', 'mo').replace(' month', 'mo')
      .replace(' years', 'y').replace(' year', 'y');
  } catch {
    return '';
  }
};

/** Render comment text with @mentions as clickable links */
const CommentText = ({ text, mentions, className }: { text: string; mentions?: CommentMention[]; className?: string }) => {
  const navigate = useNavigate();
  if (!mentions?.length) return <span className={className}>{text}</span>;

  // Build a regex matching all @username occurrences
  const mentionMap = new Map(mentions.map((m) => [m.username.toLowerCase(), m]));
  const pattern = new RegExp(`(@\\w+)`, 'g');
  const parts = text.split(pattern);

  return (
    <span className={className}>
      {parts.map((part, i) => {
        if (part.startsWith('@')) {
          const username = part.slice(1).toLowerCase();
          const mention = mentionMap.get(username);
          if (mention) {
            return (
              <button
                key={i}
                onClick={(e) => { e.stopPropagation(); navigate(`/profile/${mention.userId}`); }}
                className="font-semibold text-primary hover:underline"
              >
                {part}
              </button>
            );
          }
        }
        return <span key={i}>{part}</span>;
      })}
    </span>
  );
};

/** Mention suggestion dropdown */
const MentionSuggestions = ({
  query,
  onSelect,
  position,
}: {
  query: string;
  onSelect: (user: { id: string; username: string; name: string }) => void;
  position: { top: number; left: number };
}) => {
  const users = useMemo(() => {
    if (!query) return [];
    const q = query.toLowerCase();
    return getAllUsers()
      .filter((u) => u.username.toLowerCase().includes(q) || u.name.toLowerCase().includes(q))
      .slice(0, 5);
  }, [query]);

  if (users.length === 0) return null;

  return (
    <div
      className="absolute z-[70] w-56 rounded-lg border border-border bg-card shadow-lg overflow-hidden"
      style={{ bottom: position.top, left: position.left }}
    >
      {users.map((u) => (
        <button
          key={u.id}
          onClick={() => onSelect({ id: u.id, username: u.username, name: u.name })}
          className="flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-secondary transition-colors"
        >
          <UserAvatar avatarUrl={u.avatarUrl} initials={u.initials} className="h-6 w-6 shrink-0" fallbackClassName="text-[8px]" />
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold text-foreground truncate">{u.name}</p>
            <p className="text-[10px] text-muted-foreground truncate">@{u.username}</p>
          </div>
        </button>
      ))}
    </div>
  );
};

function scoreComment(c: FeedComment, repliesMap: Map<string, FeedComment[]>): number {
  return c.likes.length * 3 + (repliesMap.get(c.id)?.length ?? 0) * 2;
}

interface SingleCommentProps {
  comment: FeedComment;
  replies: FeedComment[];
  isLiked: boolean;
  canDelete: boolean;
  isPinned: boolean;
  canPin: boolean;
  currentUserId: string;
  postAuthorId?: string;
  onLike: () => void;
  onDelete?: () => void;
  onDeleteReply?: (replyId: string) => void;
  onLikeReply?: (replyId: string) => void;
  onReply: (authorName: string, commentId: string) => void;
  onPin?: () => void;
  onUnpin?: () => void;
  onReport?: (commentId: string) => void;
  onReportReply?: (replyId: string) => void;
}

const SingleComment = ({
  comment, replies, isLiked, canDelete, isPinned, canPin, currentUserId, postAuthorId,
  onLike, onDelete, onDeleteReply, onLikeReply, onReply, onPin, onUnpin, onReport, onReportReply,
}: SingleCommentProps) => {
  const [showReplies, setShowReplies] = useState(false);
  const commentUser = getUserById(comment.authorId);

  const canDeleteReply = (reply: FeedComment) => {
    if (reply.authorId === currentUserId) return true;
    if (postAuthorId === currentUserId) return true;
    const user = getUserById(currentUserId);
    return user?.role === 'admin';
  };

  return (
    <div>
      {isPinned && (
        <div className="mb-1 flex items-center gap-1 text-[10px] text-primary font-medium">
          <Pin className="h-2.5 w-2.5" /> Pinned
        </div>
      )}
      <div className="group flex gap-3">
        <UserAvatar
          avatarUrl={commentUser?.avatarUrl}
          initials={comment.authorInitials}
          className="h-8 w-8 shrink-0 ring-1 ring-border"
          fallbackClassName="text-[10px]"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="text-sm font-semibold text-foreground">{comment.authorName}</span>
            <span className="text-[11px] text-muted-foreground">{formatTime(comment.createdAt)}</span>
          </div>
          <p className="mt-0.5 text-sm leading-relaxed text-muted-foreground">
            <CommentText text={comment.text} mentions={comment.mentions} />
          </p>
          <div className="mt-1 flex items-center gap-4">
            <button
              onClick={onLike}
              className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors active:scale-90"
            >
              <Heart className={cn('h-3 w-3 transition-all', isLiked ? 'fill-red-500 text-red-500 scale-110' : '')} />
              {comment.likes.length > 0 && <span>{comment.likes.length}</span>}
            </button>
            <button
              onClick={() => onReply(comment.authorName, comment.id)}
              className="text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Reply
            </button>
            {canPin && (
              isPinned ? (
                <button onClick={onUnpin} className="text-[11px] text-primary hover:text-primary/80 transition-colors">
                  Unpin
                </button>
              ) : (
                <button onClick={onPin} className="text-[11px] text-muted-foreground/50 hover:text-primary transition-all">
                  Pin
                </button>
              )
            )}
            {canDelete && onDelete && (
              <button
                onClick={onDelete}
                className="text-muted-foreground/50 transition-all hover:text-destructive"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            )}
            {comment.authorId !== currentUserId && onReport && (
              <button
                onClick={() => onReport(comment.id)}
                className="text-muted-foreground/50 transition-all hover:text-orange-500"
              >
                <Flag className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>
      </div>

      {replies.length > 0 && (
        <div className="ml-11 mt-2">
          <button
            onClick={() => setShowReplies(!showReplies)}
            className="mb-2 flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80 transition-colors"
          >
            <div className="mr-1 h-px w-6 bg-muted-foreground/30" />
            {showReplies ? (
              <><ChevronUp className="h-3 w-3" /> Hide {replies.length === 1 ? 'reply' : `${replies.length} replies`}</>
            ) : (
              <><ChevronDown className="h-3 w-3" /> View {replies.length === 1 ? '1 reply' : `${replies.length} replies`}</>
            )}
          </button>
          {showReplies && (
            <div className="space-y-3 animate-in slide-in-from-top-1 duration-150">
              {replies.map((reply) => {
                const replyUser = getUserById(reply.authorId);
                const replyLiked = reply.likes.includes(currentUserId);
                const replyDeletable = canDeleteReply(reply);
                return (
                  <div key={reply.id} className="group/reply flex gap-2.5">
                    <UserAvatar
                      avatarUrl={replyUser?.avatarUrl}
                      initials={reply.authorInitials}
                      className="h-6 w-6 shrink-0 ring-1 ring-border"
                      fallbackClassName="text-[8px]"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs font-semibold text-foreground">{reply.authorName}</span>
                        <span className="text-[10px] text-muted-foreground">{formatTime(reply.createdAt)}</span>
                      </div>
                      <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                        <CommentText text={reply.text} mentions={reply.mentions} />
                      </p>
                      <div className="mt-1 flex items-center gap-3">
                        <button
                          onClick={() => onLikeReply?.(reply.id)}
                          className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors active:scale-90"
                        >
                          <Heart className={cn('h-2.5 w-2.5 transition-all', replyLiked ? 'fill-red-500 text-red-500' : '')} />
                          {reply.likes.length > 0 && <span>{reply.likes.length}</span>}
                        </button>
                        {replyDeletable && onDeleteReply && (
                          <button
                            onClick={() => onDeleteReply(reply.id)}
                            className="text-muted-foreground/50 transition-all hover:text-destructive"
                          >
                            <Trash2 className="h-2.5 w-2.5" />
                          </button>
                        )}
                        {reply.authorId !== currentUserId && onReportReply && (
                          <button
                            onClick={() => onReportReply(reply.id)}
                            className="text-muted-foreground/50 transition-all hover:text-orange-500"
                          >
                            <Flag className="h-2.5 w-2.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const CommentModal = ({
  open, onClose, comments, onSubmit, onDeleteComment, onToggleCommentLike, onPinComment,
  onReportComment, onHideComment,
  pinnedCommentId, postAuthorName, postAuthorId, currentUserId: propUserId,
  canPostComment: canPostCommentFn, onRecordCommentTime,
}: CommentModalProps) => {
  const { language } = usePreferences();
  const [text, setText] = useState('');
  const [replyTo, setReplyTo] = useState<{ name: string; commentId: string } | null>(null);
  const [sortMode, setSortMode] = useState<SortMode>('top');
  const [mentionQuery, setMentionQuery] = useState('');
  const [showMentions, setShowMentions] = useState(false);
  const [collectedMentions, setCollectedMentions] = useState<CommentMention[]>([]);
  const [reportingCommentId, setReportingCommentId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { currentUserId: sessionUserId } = useSession();
  const currentUserId = propUserId || sessionUserId || '';
  const currentUser = currentUserId ? getUserById(currentUserId) : undefined;

  // Can pin: post author, or club/coach roles on their own posts
  const canPin = !!postAuthorId && postAuthorId === currentUserId;

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  useEffect(() => {
    if (open && scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [open, comments.length]);

  if (!open) return null;

  const handleTextChange = (val: string) => {
    setText(val);
    // Detect @mention
    const cursorPos = inputRef.current?.selectionStart ?? val.length;
    const textBeforeCursor = val.slice(0, cursorPos);
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);
    if (mentionMatch) {
      setMentionQuery(mentionMatch[1]);
      setShowMentions(true);
    } else {
      setShowMentions(false);
      setMentionQuery('');
    }
  };

  const handleSelectMention = (user: { id: string; username: string; name: string }) => {
    const cursorPos = inputRef.current?.selectionStart ?? text.length;
    const textBeforeCursor = text.slice(0, cursorPos);
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);
    if (mentionMatch) {
      const before = textBeforeCursor.slice(0, mentionMatch.index);
      const after = text.slice(cursorPos);
      const newText = `${before}@${user.username} ${after}`;
      setText(newText);
      setCollectedMentions((prev) => {
        if (prev.some((m) => m.userId === user.id)) return prev;
        return [...prev, { userId: user.id, username: user.username }];
      });
    }
    setShowMentions(false);
    setMentionQuery('');
    inputRef.current?.focus();
  };

  const handleSubmit = () => {
    if (!text.trim()) return;
    // Objectionable-content filter (Apple Guideline 1.2)
    const moderation = moderateText(text);
    if (!moderation.allowed) {
      toast.error(moderationMessage(moderation, language === 'fr' ? 'fr' : 'en'));
      return;
    }
    // Anti-spam check
    if (canPostCommentFn) {
      const check = canPostCommentFn(text);
      if (!check.allowed) {
        toast.error(check.message ?? 'Cannot post comment right now.');
        return;
      }
    }
    // Extract mentions from the final text
    const finalMentions = collectedMentions.filter((m) =>
      text.toLowerCase().includes(`@${m.username.toLowerCase()}`)
    );
    onSubmit(text, replyTo?.commentId, finalMentions.length ? finalMentions : undefined);
    onRecordCommentTime?.(text);
    setText('');
    setReplyTo(null);
    setCollectedMentions([]);
  };

  const handleReply = (authorName: string, commentId: string) => {
    setReplyTo({ name: authorName, commentId });
    inputRef.current?.focus();
  };

  const canDelete = (comment: FeedComment) => {
    if (!currentUserId) return false;
    if (comment.authorId === currentUserId) return true;
    if (postAuthorId === currentUserId) return true;
    const user = getUserById(currentUserId);
    return user?.role === 'admin';
  };

  // Filter hidden comments for non-admin / non-post-author
  const isAdmin = currentUserId ? getUserById(currentUserId)?.role === 'admin' : false;
  const canSeeHidden = isAdmin || postAuthorId === currentUserId;
  const visibleComments = canSeeHidden ? comments : comments.filter((c) => !c.hidden);

  const topLevel = visibleComments.filter((c) => !c.parentCommentId);
  const repliesMap = new Map<string, FeedComment[]>();
  visibleComments.filter((c) => c.parentCommentId).forEach((c) => {
    const arr = repliesMap.get(c.parentCommentId!) ?? [];
    arr.push(c);
    repliesMap.set(c.parentCommentId!, arr);
  });

  const handleReportComment = (commentId: string) => {
    setReportingCommentId(commentId);
  };

  const handleReportReasonSelect = (reason: CommentReportReason) => {
    if (reportingCommentId) {
      onReportComment?.(reportingCommentId, reason);
      toast.success('Comment reported');
      setReportingCommentId(null);
    }
  };

  // Sort top-level comments
  const pinnedComment = pinnedCommentId ? topLevel.find((c) => c.id === pinnedCommentId) : undefined;
  const unpinnedTopLevel = topLevel.filter((c) => c.id !== pinnedCommentId);
  const sortedTopLevel = [...unpinnedTopLevel].sort((a, b) => {
    if (sortMode === 'top') {
      const scoreA = scoreComment(a, repliesMap);
      const scoreB = scoreComment(b, repliesMap);
      if (scoreB !== scoreA) return scoreB - scoreA;
    }
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  // Pinned comment always first
  const orderedComments = pinnedComment ? [pinnedComment, ...sortedTopLevel] : sortedTopLevel;

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/60 animate-in fade-in duration-150" onClick={onClose}>
      <div
        className="mx-auto flex w-full max-w-lg flex-col rounded-t-2xl bg-card animate-in slide-in-from-bottom duration-200"
        style={{ maxHeight: '75vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle */}
        <div className="flex items-center justify-center py-3">
          <div className="h-1 w-10 rounded-full bg-muted-foreground/30" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-4 pb-3">
          <h3 className="text-base font-semibold text-foreground">
            Comments
            {comments.length > 0 && (
              <span className="ml-1.5 text-sm font-normal text-muted-foreground">({comments.length})</span>
            )}
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSortMode(sortMode === 'top' ? 'newest' : 'top')}
              className="flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
            >
              <ArrowUpDown className="h-3 w-3" />
              {sortMode === 'top' ? 'Top' : 'Newest'}
            </button>
            <button onClick={onClose} className="rounded-full p-1 text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Comments list */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto overscroll-contain px-4 py-3">
          {orderedComments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-secondary">
                <Send className="h-5 w-5 text-muted-foreground" />
              </div>
              <p className="text-sm font-semibold text-foreground">No comments yet</p>
              <p className="mt-1 text-xs text-muted-foreground">Start the conversation</p>
            </div>
          ) : (
            <div className="space-y-4">
              {orderedComments.map((c) => (
                <SingleComment
                  key={c.id}
                  comment={c}
                  replies={repliesMap.get(c.id) ?? []}
                  isLiked={c.likes.includes(currentUserId)}
                  canDelete={canDelete(c)}
                  isPinned={c.id === pinnedCommentId}
                  canPin={canPin}
                  currentUserId={currentUserId}
                  postAuthorId={postAuthorId}
                  onLike={() => onToggleCommentLike?.(c.id)}
                  onDelete={canDelete(c) ? () => onDeleteComment?.(c.id) : undefined}
                  onDeleteReply={(replyId) => onDeleteComment?.(replyId)}
                  onLikeReply={(replyId) => onToggleCommentLike?.(replyId)}
                  onReply={handleReply}
                  onPin={() => onPinComment?.(c.id)}
                  onUnpin={() => onPinComment?.(undefined)}
                  onReport={handleReportComment}
                  onReportReply={handleReportComment}
                />
              ))}
            </div>
          )}
        </div>

        {/* Reply context */}
        {replyTo && (
          <div className="flex items-center gap-2 border-t border-border/50 bg-secondary/50 px-4 py-2">
            <Reply className="h-3.5 w-3.5 text-primary" />
            <span className="text-xs text-muted-foreground">
              Replying to <span className="font-semibold text-foreground">{replyTo.name}</span>
            </span>
            <button onClick={() => setReplyTo(null)} className="ml-auto text-muted-foreground hover:text-foreground">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {/* Composer */}
        <div className={cn('relative border-t border-border px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]', replyTo && 'border-t-0')}>
          {/* Mention suggestions */}
          {showMentions && (
            <MentionSuggestions
              query={mentionQuery}
              onSelect={handleSelectMention}
              position={{ top: 56, left: 52 }}
            />
          )}
          <div className="flex items-center gap-3">
            <UserAvatar
              avatarUrl={currentUser?.avatarUrl}
              initials={currentUser?.initials ?? '?'}
              className="h-8 w-8 shrink-0 ring-1 ring-border"
              fallbackClassName="text-[10px]"
            />
            <div className="flex flex-1 items-center gap-2 rounded-full bg-secondary px-4 py-2">
              <input
                ref={inputRef}
                value={text}
                onChange={(e) => handleTextChange(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                placeholder={replyTo ? `Reply to ${replyTo.name}...` : `Reply to ${postAuthorName}...`}
                className="min-w-0 flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
              />
              <button
                onClick={handleSubmit}
                disabled={!text.trim()}
                className={cn(
                  'shrink-0 rounded-full p-1 transition-all',
                  text.trim() ? 'text-primary hover:bg-primary/10' : 'text-muted-foreground/40'
                )}
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Report reason picker */}
        {reportingCommentId && (
          <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 animate-in fade-in duration-100" onClick={() => setReportingCommentId(null)}>
            <div className="mx-6 w-full max-w-xs rounded-xl bg-card border border-border p-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <h4 className="mb-3 text-sm font-semibold text-foreground">Report comment</h4>
              <div className="space-y-1">
                {REPORT_REASONS.map((r) => (
                  <button
                    key={r.value}
                    onClick={() => handleReportReasonSelect(r.value)}
                    className="w-full rounded-lg px-3 py-2 text-left text-sm text-foreground hover:bg-secondary transition-colors"
                  >
                    {r.label}
                  </button>
                ))}
              </div>
              <button onClick={() => setReportingCommentId(null)} className="mt-3 w-full rounded-lg py-2 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommentModal;
